import React, { useEffect, useState } from "react";
import { PencilSimple, Trash, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const GLASSES = ["Vaso largo", "Copa tulipán", "Vaso de yarda"];
const EMPTY_BEER = { name: "", abv: "", character: "", glass: "Vaso largo", image_url: "", dishes: [], sort_order: 999 };
const EMPTY_DISH = { name: "", description: "", image_url: "", price_medium: "", price_large: "", sort_order: 999 };

function BeerModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...initial,
    dishesText: (initial.dishes || [])
      .map((d) => {
        const n = typeof d === "string" ? d : d.name;
        const p = typeof d === "object" && d.price != null ? d.price : null;
        return p != null ? `${n} | ${p}` : n;
      })
      .join("\n"),
  });
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    const dishes = (form.dishesText || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split("|");
        const name = parts[0].trim();
        const priceRaw = parts[1] != null ? parseFloat(parts[1].replace(/[^0-9.]/g, "")) : NaN;
        return { name, price: isNaN(priceRaw) ? null : priceRaw };
      });
    const payload = {
      name: form.name, abv: form.abv || "", character: form.character || "",
      glass: form.glass, image_url: form.image_url || "",
      price: form.price !== "" && form.price != null ? parseFloat(form.price) : null,
      dishes,
      sort_order: parseInt(form.sort_order) || 999,
    };
    try {
      if (editing) await api.put(`/admin/menu/beers/${form.id}`, payload);
      else await api.post("/admin/menu/beers", payload);
      toast.success(editing ? "Cerveza del menú actualizada" : "Cerveza agregada al menú");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="menu-beer-modal">
      <div className="w-full max-w-2xl bg-[#141414] border border-white/15 max-h-[90vh] overflow-auto cg-scroll">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#141414]">
          <h2 className="font-display text-3xl">{editing ? "Editar cerveza del menú" : "Nueva cerveza del menú"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-menu-beer"><X size={22} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div><label className="overline block mb-2">Nombre de la cerveza</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="mb-name" /></div>
          <div><label className="overline block mb-2">ABV (ej: 5.0%) — opcional</label>
            <input value={form.abv} onChange={(e) => set("abv", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="mb-abv" /></div>
          <div><label className="overline block mb-2">Precio de venta ($) — opcional</label>
            <input type="number" step="0.01" value={form.price ?? ""} onChange={(e) => set("price", e.target.value)} placeholder="Ej: 3.5" className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="mb-price" /></div>
          <div className="sm:col-span-2"><label className="overline block mb-2">Carácter (aroma / sabor)</label>
            <input value={form.character} onChange={(e) => set("character", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="mb-character" /></div>
          <div><label className="overline block mb-2">Cristalería</label>
            <select value={form.glass} onChange={(e) => set("glass", e.target.value)} className="w-full bg-[#141414] border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="mb-glass">
              {GLASSES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select></div>
          <div><label className="overline block mb-2">Orden (menor = primero)</label>
            <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="mb-order" /></div>
          <div className="sm:col-span-2"><label className="overline block mb-2">URL de imagen (botella)</label>
            <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="mb-image" /></div>
          <div className="sm:col-span-2"><label className="overline block mb-2">Comidas / tapas que la acompañan — una por línea. Precio opcional con "|" (ej: Tequeños | 4)</label>
            <textarea rows={4} value={form.dishesText} onChange={(e) => set("dishesText", e.target.value)} placeholder={"Tapas de plátano criollo | 4\nTapas de queso | 3.5"} className="w-full bg-transparent border border-white/20 focus:border-[#D97736] outline-none p-3 text-white text-sm" data-testid="mb-dishes" /></div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 border border-white/20 hover:border-white/40 uppercase tracking-wide text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-6 py-3 bg-[#D97736] hover:bg-[#E88D50] disabled:opacity-60 text-black font-semibold uppercase tracking-wide text-sm" data-testid="save-menu-beer">{saving ? "Guardando..." : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

function DishModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    const payload = { name: form.name, description: form.description || "", image_url: form.image_url || "", price_medium: form.price_medium !== "" && form.price_medium != null ? parseFloat(form.price_medium) : null, price_large: form.price_large !== "" && form.price_large != null ? parseFloat(form.price_large) : null, sort_order: parseInt(form.sort_order) || 999 };
    try {
      if (editing) await api.put(`/admin/menu/tastings/${form.id}`, payload);
      else await api.post("/admin/menu/tastings", payload);
      toast.success(editing ? "Plato actualizado" : "Plato agregado");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="menu-dish-modal">
      <div className="w-full max-w-xl bg-[#141414] border border-white/15 max-h-[90vh] overflow-auto cg-scroll">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#141414]">
          <h2 className="font-display text-3xl">{editing ? "Editar plato" : "Nuevo plato de degustación"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-menu-dish"><X size={22} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div><label className="overline block mb-2">Nombre del plato</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="md-name" /></div>
          <div><label className="overline block mb-2">Descripción</label>
            <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-transparent border border-white/20 focus:border-[#D97736] outline-none p-3 text-white text-sm" data-testid="md-description" /></div>
          <div><label className="overline block mb-2">URL de imagen</label>
            <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="md-image" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="overline block mb-2">Precio Mediano ($)</label>
              <input type="number" step="0.01" value={form.price_medium} onChange={(e) => set("price_medium", e.target.value)} placeholder="Ej: 8" className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="md-price-medium" /></div>
            <div><label className="overline block mb-2">Precio Grande ($)</label>
              <input type="number" step="0.01" value={form.price_large} onChange={(e) => set("price_large", e.target.value)} placeholder="Ej: 12" className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="md-price-large" /></div>
          </div>
          <div><label className="overline block mb-2">Orden</label>
            <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-2.5 text-white" data-testid="md-order" /></div>
        </div>
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 border border-white/20 hover:border-white/40 uppercase tracking-wide text-sm">Cancelar</button>
          <button onClick={save} disabled={saving} className="px-6 py-3 bg-[#D97736] hover:bg-[#E88D50] disabled:opacity-60 text-black font-semibold uppercase tracking-wide text-sm" data-testid="save-menu-dish">{saving ? "Guardando..." : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}

export const MenuAdmin = () => {
  const [beers, setBeers] = useState([]);
  const [tastings, setTastings] = useState([]);
  const [editBeer, setEditBeer] = useState(null);
  const [editDish, setEditDish] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/menu").then((r) => { setBeers(r.data.beers || []); setTastings(r.data.tastings || []); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const delBeer = async (id) => { try { await api.delete(`/admin/menu/beers/${id}`); toast.success("Cerveza eliminada del menú"); load(); } catch { toast.error("Error al eliminar"); } };
  const delDish = async (id) => { try { await api.delete(`/admin/menu/tastings/${id}`); toast.success("Plato eliminado"); load(); } catch { toast.error("Error al eliminar"); } };
  const doDelete = async () => {
    if (!confirm) return;
    if (confirm.kind === "beer") await delBeer(confirm.id);
    else await delDish(confirm.id);
    setConfirm(null);
  };

  return (
    <div className="space-y-12" data-testid="menu-admin">
      {/* Cervezas del menú */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-3xl">Cervezas del menú <span className="text-gray-500 text-lg">({beers.length})</span></h3>
          <button onClick={() => setEditBeer(EMPTY_BEER)} className="flex items-center gap-2 bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold px-4 py-2.5 uppercase tracking-wide text-sm" data-testid="add-menu-beer-btn">
            <Plus size={16} weight="bold" /> Cerveza
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {beers.map((b) => (
            <div key={b.id} className="flex gap-4 border border-white/10 bg-[#111] p-4" data-testid={`menu-beer-row-${b.id}`}>
              {b.image_url ? <img src={b.image_url} alt="" className="w-14 h-20 object-contain shrink-0" /> : <div className="w-14 h-20 bg-white/5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium">{b.name} {b.abv && <span className="text-[#F5A623] text-xs">{b.abv}</span>}</div>
                <div className="text-[#D97736] text-sm italic">{b.character}</div>
                <div className="text-gray-500 text-xs mt-1">🍷 {b.glass} · {(b.dishes || []).length} tapas</div>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => setEditBeer(b)} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#D97736] hover:text-[#D97736]" data-testid={`edit-menu-beer-${b.id}`}><PencilSimple size={16} /></button>
                <button onClick={() => setConfirm({ kind: "beer", id: b.id, name: b.name })} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-red-500 hover:text-red-400" data-testid={`del-menu-beer-${b.id}`}><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platos de degustación */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-3xl">Platos de degustación <span className="text-gray-500 text-lg">({tastings.length})</span></h3>
          <button onClick={() => setEditDish(EMPTY_DISH)} className="flex items-center gap-2 bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold px-4 py-2.5 uppercase tracking-wide text-sm" data-testid="add-menu-dish-btn">
            <Plus size={16} weight="bold" /> Plato
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tastings.map((t) => (
            <div key={t.id} className="flex gap-4 border border-white/10 bg-[#111] p-4" data-testid={`menu-dish-row-${t.id}`}>
              {t.image_url ? <img src={t.image_url} alt="" className="w-16 h-16 object-cover shrink-0" /> : <div className="w-16 h-16 bg-white/5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium">{t.name} {(t.price_medium != null || t.price_large != null) ? <span className="text-[#F5A623] text-sm">· {t.price_medium != null ? `M $${t.price_medium}` : ""}{t.price_medium != null && t.price_large != null ? " / " : ""}{t.price_large != null ? `G $${t.price_large}` : ""}</span> : (t.price != null && <span className="text-[#F5A623] text-sm">· ${t.price}</span>)}</div>
                <div className="text-gray-400 text-xs mt-1">{t.description}</div>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => setEditDish(t)} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-[#D97736] hover:text-[#D97736]" data-testid={`edit-menu-dish-${t.id}`}><PencilSimple size={16} /></button>
                <button onClick={() => setConfirm({ kind: "dish", id: t.id, name: t.name })} className="w-9 h-9 flex items-center justify-center border border-white/15 hover:border-red-500 hover:text-red-400" data-testid={`del-menu-dish-${t.id}`}><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editBeer && <BeerModal initial={editBeer} onClose={() => setEditBeer(null)} onSaved={load} />}
      {editDish && <DishModal initial={editDish} onClose={() => setEditDish(null)} onSaved={load} />}

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#141414] border border-white/15 p-6 max-w-sm w-full" data-testid="menu-delete-confirm">
            <h3 className="font-display text-3xl">¿Eliminar del menú?</h3>
            <p className="text-gray-400 text-sm mt-2">Vas a eliminar <span className="text-white">{confirm.name}</span>. Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setConfirm(null)} className="px-5 py-3 border border-white/20 uppercase tracking-wide text-sm">Cancelar</button>
              <button onClick={doDelete} className="px-5 py-3 bg-red-600 hover:bg-red-500 text-white uppercase tracking-wide text-sm transition-colors" data-testid="confirm-menu-delete">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
